import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb, Clock, CheckCircle, XCircle, AlertCircle, 
  Eye, MessageSquare, Filter
} from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { api } from '../../lib/auth';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import { LogoIcon } from '../../components/Logo';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Requests' },
  { value: 'pending', label: 'Pending', color: 'text-yellow-400' },
  { value: 'reviewed', label: 'Reviewed', color: 'text-blue-400' },
  { value: 'approved', label: 'Approved', color: 'text-green-400' },
  { value: 'declined', label: 'Declined', color: 'text-red-400' },
];

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
  reviewed: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: AlertCircle },
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
  declined: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
};

const BUDGET_OPTIONS = {
  'under_500': 'Under $500',
  '500_1000': '$500 - $1,000',
  '1000_2500': '$1,000 - $2,500',
  '2500_5000': '$2,500 - $5,000',
  'over_5000': 'Over $5,000',
  'not_sure': 'Not sure yet',
};

export default function AdminServiceRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await api.getServiceRequests(params);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    setUpdating(true);
    try {
      await api.updateServiceRequestStatus(requestId, newStatus, adminNotes);
      toast.success(`Request ${newStatus}`);
      setSelectedRequest(null);
      setAdminNotes('');
      loadRequests();
    } catch (error) {
      toast.error('Failed to update request');
    } finally {
      setUpdating(false);
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Lightbulb className="w-8 h-8 text-primary" />
              <h1 className="font-heading text-3xl font-bold text-white">
                Service Requests
              </h1>
              {pendingCount > 0 && (
                <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium">
                  {pendingCount} pending
                </span>
              )}
            </div>
          </div>
          <p className="text-gray-400">
            Review and respond to service requests from clients
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-4 mb-6"
        >
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 input-dark" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className={opt.color}>{opt.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500">
              {requests.length} request{requests.length !== 1 ? 's' : ''}
            </span>
          </div>
        </motion.div>

        {/* Requests Table */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 glass rounded-2xl">
            <LogoIcon size="lg" className="mx-auto mb-4 opacity-50" />
            <p className="text-gray-400">No service requests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request, i) => {
              const statusStyle = STATUS_STYLES[request.status] || STATUS_STYLES.pending;
              const StatusIcon = statusStyle.icon;
              
              return (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="glass rounded-xl p-5"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white text-lg">
                          {request.service_name}
                        </h3>
                        <div className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full ${statusStyle.bg}`}>
                          <StatusIcon className={`w-3.5 h-3.5 ${statusStyle.text}`} />
                          <span className={`text-xs font-medium capitalize ${statusStyle.text}`}>
                            {request.status}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {request.description}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                        <span>From: <span className="text-gray-300">{request.user_email}</span></span>
                        <span>Submitted: {formatDate(request.created_at)}</span>
                        {request.budget_range && (
                          <span>Budget: <span className="text-accent">{BUDGET_OPTIONS[request.budget_range] || request.budget_range}</span></span>
                        )}
                        <span className={`capitalize ${request.urgency === 'urgent' ? 'text-orange-400' : ''}`}>
                          {request.urgency}
                        </span>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedRequest(request)}
                      className="text-gray-400 hover:text-white"
                      data-testid={`view-request-${request.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Review
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Request Detail Dialog */}
        <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent className="bg-card border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white text-xl">
                {selectedRequest?.service_name}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Requested by {selectedRequest?.user_email} on {selectedRequest && formatDate(selectedRequest.created_at)}
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Description</h4>
                  <p className="text-gray-400 text-sm bg-white/5 rounded-lg p-4">
                    {selectedRequest.description}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-1">Budget</h4>
                    <p className="text-sm text-white">
                      {BUDGET_OPTIONS[selectedRequest.budget_range] || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-1">Urgency</h4>
                    <p className={`text-sm capitalize ${selectedRequest.urgency === 'urgent' ? 'text-orange-400' : 'text-white'}`}>
                      {selectedRequest.urgency}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-1">Status</h4>
                    <p className={`text-sm capitalize ${STATUS_STYLES[selectedRequest.status]?.text}`}>
                      {selectedRequest.status}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Admin Response (optional)
                  </h4>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add a note for the client about this request..."
                    className="w-full h-24 px-4 py-3 bg-black/20 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-primary/50 focus:outline-none resize-none"
                    data-testid="admin-notes-input"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  {selectedRequest.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleStatusUpdate(selectedRequest.id, 'reviewed')}
                        variant="outline"
                        className="flex-1 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        disabled={updating}
                      >
                        Mark as Reviewed
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(selectedRequest.id, 'approved')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        disabled={updating}
                        data-testid="approve-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(selectedRequest.id, 'declined')}
                        variant="outline"
                        className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                        disabled={updating}
                        data-testid="decline-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Decline
                      </Button>
                    </>
                  )}
                  {selectedRequest.status === 'reviewed' && (
                    <>
                      <Button
                        onClick={() => handleStatusUpdate(selectedRequest.id, 'approved')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        disabled={updating}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(selectedRequest.id, 'declined')}
                        variant="outline"
                        className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                        disabled={updating}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Decline
                      </Button>
                    </>
                  )}
                  {(selectedRequest.status === 'approved' || selectedRequest.status === 'declined') && (
                    <Button
                      onClick={() => handleStatusUpdate(selectedRequest.id, 'pending')}
                      variant="outline"
                      className="flex-1 border-white/20 text-gray-300"
                      disabled={updating}
                    >
                      Reopen Request
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
